import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import { Link } from "react-router-dom";

import {
  fetchSlideClassification,
  fetchSpecimenClassification,
  wipeAI,
} from "shared/actions/aiActions";
import {
  fetchCaseByAccessionNumber,
  slideDetail,
  wipeSlideDetail,
} from "shared/actions/slideDetailActions";

import ErrorPane from "components/ErrorPane";
import PageLayout from "components/PageLayout";
import FourOhFourPage from "components/pages/FourOhFourPage";
import Spinner from "components/Spinner";
import ToastList from "components/toasts/ToastList";
import { slideClassification } from "components/viewer/dialogs/utils";
import SlideViewer from "components/viewer/SlideViewer";
import { useHasAILoaded, useIsAILoading } from "hooks/aiHooks";
import useViewerItems from "hooks/useViewerItems";
import { selectSlideClassification } from "selectors/classificationSelector";
import { useAppDispatch, useAppSelector } from "store/hooks";

const SlideViewerPage = (_props: any) => {
  const authObject = useAppSelector(({ auth }) => auth);
  const sysObject = useAppSelector(({ sys }) => sys);
  const caseDetailObject = useAppSelector(({ caseDetail }) => caseDetail);
  const slideDetailObject = useAppSelector(({ slideDetail }) => slideDetail);

  const aiObject = useAppSelector((state) =>
    selectSlideClassification(state, slideDetailObject?.id)
  );

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const params = useParams<{
    case_id: string;
    slide_id: string;
    document_id: string;
  }>();

  let { case_id = "", slide_id = "", document_id = "" } = params;

  // If case_id isn't all digits, it's to be considered an accession ID
  let accession_id = "";
  if (!case_id.match(/^\d+$/)) {
    accession_id = case_id;
    case_id = "";
  }

  const caseId = case_id ? parseInt(case_id) : undefined;
  const slideId = slide_id ? parseInt(slide_id) : undefined;
  const documentId = document_id ? parseInt(document_id) : undefined;

  const error: Error | undefined =
    caseDetailObject.error || slideDetailObject.error;

  const viewerItems = useViewerItems(caseId);

  const specifiedOrFirstItem = slideId
    ? {
        id: slideId,
        type: "slides",
      }
    : documentId
    ? {
        id: documentId,
        type: "documents",
      }
    : viewerItems.at(0);

  const hasOrderedDocuments = caseDetailObject.documents.some(
    (d) => d.status === "uploaded" && d.lab_order_id
  );

  // Moving all these "wipe" actions to this one useEffect call where they won't wreak havoc.
  useEffect(() => {
    // Runs once when mounting
    dispatch(wipeSlideDetail());
    dispatch(wipeAI()); // TODO: "WIPE AI" is a code smell
    return () => {
      // Runs once on unmounting, in reversed order for clarity
      dispatch(wipeAI()); // TODO: "WIPE AI" is a code smell
      dispatch(wipeSlideDetail());
    };
  }, [dispatch]);

  useEffect(() => {
    if (specifiedOrFirstItem?.id && specifiedOrFirstItem?.type === "slides") {
      dispatch(slideDetail(specifiedOrFirstItem.id));
    }
  }, [specifiedOrFirstItem?.id, specifiedOrFirstItem?.type]);

  useEffect(() => {
    if (documentId && !slideId) {
      dispatch(wipeSlideDetail());
      dispatch(wipeAI()); // TODO: "WIPE AI" is a code smell
    }
  }, [slideId, documentId]);

  useEffect(() => {
    if (!caseId && slideDetailObject.case_id) {
      navigate(
        `/cases/${slideDetailObject.case_id}/slides/${slideDetailObject.id}`,
        { replace: true }
      );
    } else if (!caseId && slideDetailObject.caseByAccessionResult) {
      navigate(`/cases/${slideDetailObject.caseByAccessionResult.id}`, {
        replace: true,
      });
    }
  }, [
    caseId,
    slideDetailObject.caseByAccessionResult,
    // props.history,
    // Inclusion of slideDetailObject.case_id triggers infinite loop
    slideDetailObject.case_id,
    slideDetailObject.id,
  ]);

  useEffect(() => {
    if (
      caseId &&
      !slideId &&
      !documentId &&
      !!specifiedOrFirstItem?.id &&
      !!specifiedOrFirstItem?.type
    ) {
      navigate(
        `/cases/${caseId}/${specifiedOrFirstItem?.type}/${specifiedOrFirstItem?.id}`,
        {
          replace: true,
        }
      );
    }
  }, [
    caseId,
    slideId,
    documentId,
    specifiedOrFirstItem?.id,
    specifiedOrFirstItem?.type,
  ]);

  useEffect(() => {
    if (accession_id) {
      dispatch(fetchCaseByAccessionNumber(accession_id));
    }
  }, [dispatch, accession_id, authObject.access]);

  const classificationInfo = slideClassification(slideDetailObject);
  const aiLoaded = useHasAILoaded(slideDetailObject);
  const aiLoading = useIsAILoading(slideDetailObject);

  useEffect(() => {
    if (!sysObject.features.prospectiveAI) return;

    if (caseId && slideDetailObject.specimen?.id && !aiLoaded && !aiLoading) {
      dispatch(
        fetchSpecimenClassification({
          parentLookupCaseId: caseId,
          id: slideDetailObject.specimen?.id,
        })
      );
    } else if (
      classificationInfo?.id &&
      slideDetailObject.id &&
      aiObject?.id !== classificationInfo.id &&
      !aiLoaded &&
      !aiLoading
    ) {
      // Drop back to traditional single-classification output
      dispatch(
        fetchSlideClassification({
          id: classificationInfo.id,
          slide_id: slideDetailObject.id,
        })
      );
    } else if (!classificationInfo?.id) {
      // TODO: this still doesn't belong here; code smell
      dispatch(wipeAI());
    }
  }, [
    sysObject.features.prospectiveAI,
    caseDetailObject.slides?.length,
    caseId,
    slideDetailObject.specimen?.id,
    classificationInfo?.id,
    slideDetailObject.id,
    aiObject?.id,
    aiLoaded,
    aiLoading,
  ]);

  useEffect(() => {
    dispatch(wipeAI());
  }, [slideDetailObject?.specimen?.id]);

  return (
    <>
      <Helmet>
        <title>{`Slide ${
          slideDetailObject?.id || ""
        } - Pathology Watch`}</title>
      </Helmet>
      <PageLayout>
        <ToastList />
        {slideDetailObject.noMatchingCaseByAccessionResult ? (
          <div className="FullScreenCenter">
            <div>
              Accession {accession_id} is not currently available.
              <br />
              Please contact{" "}
              <a href="mailto:support@pathologywatch.com">
                <strong>support@pathologywatch.com</strong>
              </a>{" "}
              if this is unexpected.
              <br />
              <br />
              <div style={{ marginLeft: "150px", marginRight: "150px" }}>
                <Link to={"/"}>
                  <div className="Button">HOME</div>
                </Link>
              </div>
            </div>
          </div>
        ) : !slideDetailObject.loading &&
          !error &&
          !slideDetailObject.case_id ? (
          <SlideViewer
            authObject={authObject}
            slideDetailObject={slideDetailObject}
            documentId={documentId}
            aiObject={aiObject}
          />
        ) : caseDetailObject.loading ? (
          <div className="FullScreenCenter">
            <Spinner />
          </div>
        ) : error ? (
          <div style={{ marginTop: "50px" }}>
            <ErrorPane error={error} />
          </div>
        ) : (!slideId &&
            caseDetailObject.slides.length === 0 &&
            !hasOrderedDocuments) ||
          (!slideDetailObject.loading &&
            caseDetailObject.id !== slideDetailObject.case_id) ? (
          <FourOhFourPage />
        ) : (
          <SlideViewer
            authObject={authObject}
            caseDetailObject={caseDetailObject}
            slideDetailObject={slideDetailObject}
            aiObject={aiObject}
            hasOrderedDocuments={hasOrderedDocuments}
            documentId={documentId}
          />
        )}
      </PageLayout>
      <div id="custom-toolbar" style={{ display: "none" }} />
    </>
  );
};

export default SlideViewerPage;
